
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { StoreModel } from '@/models/store';
import { StoreLocationList } from '@/collections/store_locations';
import { StoreLocationModel } from '@/models/store_location';
import { FilterColumnList } from "@/collections/filter_columns";
import { FilterColumnModel } from '@/models/filter_column';

@Component
export default class LocationFilter extends Vue {
  @Prop({ default: () => new StoreModel() })
  private store!: StoreModel;

  @Prop({ default: 'mdi-store' })
  private prependIcon!: string;

  @Prop({ default: 'max-width: 220px;' })
  private selectStyle!: string;

  @Prop({ default: () => [] })
  private initialLocationIds!: number[];

  private selectedLocationIds: Array<number | string> = ['all-location-option']; // Default to "All Locations" selected
  private loadingLocations = false;
  private locations: StoreLocationModel[] = [];
  private storeLocationList = new StoreLocationList();
  private userHasInteractedWithLocationFilter: boolean = false;

  get locationFilterOptions() {
    const isAllSelected = this.selectedLocationIds.includes('all-location-option');
    return [
      { id: 'all-location-option', name: 'All Locations (Show All)' },
      ...this.locations.map(loc => ({
        id: loc.id,
        name: loc.name,
        disabled: isAllSelected // Disable if 'All Locations' is selected
      }))
    ];
  }

  get computedLocationSelectLabel(): string {
    const actualSelections = this.selectedLocationIds.filter(id => id !== 'all-location-option');

    if (this.selectedLocationIds.includes('all-location-option')) {
        if (this.selectedLocationIds.length === 1) { // Only "All Locations" is selected
            return "All Locations (Show All)";
        } else if (actualSelections.length > 0) { // "All Locations" + specific ones (should not happen with current logic)
             return `Selected (${actualSelections.length})`; // Fallback, though ideally filtered
        }
    }

    if (actualSelections.length > 0) {
      return `Selected (${actualSelections.length})`;
    }
    
    if (this.selectedLocationIds.length === 0 && this.userHasInteractedWithLocationFilter) {
        return "All Locations Selected"; // Implies empty filter list for backend
    }
    
    return "Filter by Store Location"; // Default or when no interaction yet and empty
  }
  
  @Watch('store')
  async onStoreChanged() {
    // If the store prop changes, re-fetch locations
    // This might be useful if the component is used in contexts where the store can change
    await this.fetchLocations();
  }

  async created() {
    await this.fetchLocations();
    this.applyInitialLocationIds();
  }

  @Watch('initialLocationIds')
  private onInitialLocationIdsChanged(): void {
    this.applyInitialLocationIds();
  }

  private applyInitialLocationIds(): void {
    if (this.initialLocationIds && this.initialLocationIds.length > 0) {
      this.selectedLocationIds = [...this.initialLocationIds];
      this.userHasInteractedWithLocationFilter = true;
      this.applyFilter();
    }
  }

  private async fetchLocations() {
    if (!this.store || !this.store.id) {
      this.$emit('error', 'Store not available for fetching locations.');
      this.locations = []; // Clear locations if store is not valid
      return;
    }
    this.loadingLocations = true;
    try {
      // Assuming StoreLocationList uses the store context or can be passed a store id
      // If your StoreLocationList needs the store.id, you might need to adjust its fetch method
      // or pass the store.id to it. For now, let's assume it works like in PurchaseOrderCreate.vue
      await this.storeLocationList.fetch(); // Or: await new StoreLocationList({ storeId: this.store.id }).fetch();
      this.locations = this.storeLocationList.items;
    } catch (error) {
      console.error('Failed to load store locations:', error);
      this.$emit('error', 'Failed to load store locations');
      this.locations = []; // Clear on error
    } finally {
      this.loadingLocations = false;
    }
  }

  private getFilteredSelectionCount(parentValue: Array<string | number>): number {
    if (!parentValue) return 0;
    return parentValue.filter((id: string | number) => id !== 'all-location-option').length;
  }

  public handleMultipleLocationChange(selectedValuesFromComponent: Array<number | string>) {
    this.userHasInteractedWithLocationFilter = true;
    const allLocationsOptionId = 'all-location-option';
    const currentSelectionHasAll = selectedValuesFromComponent.includes(allLocationsOptionId);
    // const previouslyHadAll = this.selectedLocationIds.includes(allLocationsOptionId); // Keep for reference if needed, but might simplify

    if (currentSelectionHasAll) {
        // If "All Locations" is now selected by the user (it's in the component's new value array).
        // We should ensure selectedLocationIds becomes *only* ['all-location-option'].
        // This handles cases where it was just clicked, or if (somehow, despite disabling) it was co-selected.
        if (this.selectedLocationIds.length !== 1 || !this.selectedLocationIds.includes(allLocationsOptionId)) {
            this.selectedLocationIds = [allLocationsOptionId];
        }
        // If this.selectedLocationIds was already just ['all-location-option'], no change needed by this assignment.
    } else {
        // "All Locations" is NOT in the current selection from the component.
        // This means either specific items are selected, or it was deselected leaving an empty array.
        // In this case, selectedLocationIds should simply reflect the values from the component.
        this.selectedLocationIds = selectedValuesFromComponent;
    }
  }


  public applyFilter() {
    const filters = new FilterColumnList();
    // Only add a filter if specific locations are selected.
    // If 'all-location-option' is in selectedLocationIds (or selectedLocationIds is empty after user interaction),
    // it means "all locations", so we pass an empty FilterColumnList.
    const actualLocationIds = this.selectedLocationIds.filter(id => id !== 'all-location-option');

    if (actualLocationIds.length > 0) {
      const locationFilter = new FilterColumnModel();
      locationFilter.mapData({
        code: 'location_id', // Assuming this is the correct backend field for location ID
        name: 'Location',
        value: actualLocationIds,
        operator: 'eq',
        dataType: 'text' // Or 'numeric' if IDs are numbers backend-wise
      });
      filters.add(locationFilter);
    }
    
    this.$emit('filter-applied', filters);

    // Close the v-select dropdown
    if (this.$refs.locationSelect) {
      (this.$refs.locationSelect as any).isMenuActive = false;
    }
  }
}
